import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { Plus, Edit2, Trash2, CalendarDays } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import ConfirmDialog from '@/components/ConfirmDialog';
import { Badge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import { holidayService } from '@/services';

const TYPE_STYLES = {
  public: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  festival: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  optional: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  company: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
};

export default function AdminHolidays() {
  const qc = useQueryClient();
  const [year, setYear] = useState(dayjs().year());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => holidayService.list({ year }),
  });

  const items = data?.items || [];

  const remove = (h) => {
    setConfirmState({
      title: 'Delete holiday',
      message: (
        <>
          <b>{h.title}</b> on {dayjs(h.date).format('DD MMM YYYY')} will be removed from everyone's calendar.
        </>
      ),
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await holidayService.remove(h._id);
          toast.success('Holiday deleted');
          qc.invalidateQueries({ queryKey: ['holidays'] });
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed');
        }
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Holidays"
        subtitle="Announce public, festival and company holidays — they appear on every employee's calendar"
        actions={
          <button className="btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus size={16} /> Add holiday
          </button>
        }
      />

      <Card>
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays size={16} className="text-slate-400" />
          <select className="input w-32" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[dayjs().year() - 1, dayjs().year(), dayjs().year() + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <span className="text-sm text-slate-500">{items.length} holiday{items.length === 1 ? '' : 's'}</span>
        </div>

        {isLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <EmptyState title="No holidays yet" subtitle="Click “Add holiday” to announce one." />
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Date</th>
                  <th className="th">Day</th>
                  <th className="th">Holiday</th>
                  <th className="th">Type</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((h) => (
                  <tr key={h._id}>
                    <td className="td font-medium">{dayjs(h.date).format('DD MMM YYYY')}</td>
                    <td className="td text-slate-500">{dayjs(h.date).format('dddd')}</td>
                    <td className="td">
                      <div className="font-medium">{h.title}</div>
                      {h.description && <div className="text-xs text-slate-500">{h.description}</div>}
                    </td>
                    <td className="td">
                      <Badge className={`capitalize ${TYPE_STYLES[h.type] || TYPE_STYLES.public}`}>{h.type}</Badge>
                    </td>
                    <td className="td">
                      <div className="flex gap-1">
                        <button className="btn-ghost p-1" onClick={() => { setEditing(h); setShowForm(true); }}>
                          <Edit2 size={14} />
                        </button>
                        <button className="btn-ghost p-1 text-rose-600" onClick={() => remove(h)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showForm && (
        <HolidayFormModal
          holiday={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => {
            setShowForm(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['holidays'] });
          }}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function HolidayFormModal({ holiday, onClose, onSaved }) {
  const editing = !!holiday;
  const { register, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: holiday
      ? { title: holiday.title, date: holiday.date, type: holiday.type, description: holiday.description || '' }
      : { title: '', date: dayjs().format('YYYY-MM-DD'), type: 'public', description: '' },
  });

  const onSubmit = async (data) => {
    try {
      if (editing) {
        await holidayService.update(holiday._id, data);
        toast.success('Holiday updated');
      } else {
        await holidayService.create(data);
        toast.success('Holiday announced');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit holiday' : 'Add holiday'}
      size="md"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="holidayForm" className="btn-primary" disabled={isSubmitting}>
            {editing ? 'Save' : 'Announce'}
          </button>
        </>
      }
    >
      <form id="holidayForm" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div>
          <label className="label">Holiday name</label>
          <input className="input" placeholder="e.g. Diwali" {...register('title', { required: true })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Date</label>
            <input type="date" className="input" {...register('date', { required: true })} />
          </div>
          <div>
            <label className="label">Type</label>
            <select className="input" {...register('type')}>
              <option value="public">Public</option>
              <option value="festival">Festival</option>
              <option value="optional">Optional</option>
              <option value="company">Company</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Description <span className="text-slate-400 font-normal">(optional)</span></label>
          <textarea rows={3} className="input" placeholder="Any note for the team..." {...register('description')} />
        </div>
        {!editing && (
          <p className="text-xs text-slate-400">All active employees are notified when you announce a holiday.</p>
        )}
      </form>
    </Modal>
  );
}
